import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Snowflake } from '@sorare/core/src/atoms/icons/Snowflake';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { isType } from '@sorare/core/src/lib/gql';

import { PlayMissionsTaskRow } from '#src/components/missions/PlayTask/index.tsx';
import { TaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/index.tsx';
import { useTaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/useTaskClaimedDialogs.tsx';

import type { PlayMissions_currentUser } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 3 })`
  width: 100%;
`;

const Section = styled(Vertical).attrs({ gap: 1.5 })`
  width: 100%;
`;

type Props = {
  currentUser: Nullable<PlayMissions_currentUser>;
  refetch?: () => void;
};

export const PlayMissions = ({ currentUser, refetch }: Props) => {
  const setPlayTasks = currentUser?.setPlayTasks;
  const {
    onClaim,
    loadingTaskId,
    pickerDialogTask,
    setPickerDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    rewardDialogTask,
    setRewardDialogTask,
  } = useTaskClaimedDialogs();

  if (!setPlayTasks?.length) {
    return null;
  }

  return (
    <>
      <TaskClaimedDialogs
        pickerDialogTask={pickerDialogTask}
        onPickerDialogClose={() => {
          setPickerDialogTask(null);
          refetch?.();
        }}
        revealPackCardsDialogReward={revealPackCardsDialogReward}
        onRevealPackCardsDialogClose={() => {
          setRevealPackCardsDialogReward(null);
          refetch?.();
        }}
        rewardDialogTask={rewardDialogTask}
        onRewardDialogClose={() => {
          setRewardDialogTask(null);
          refetch?.();
        }}
      />
      <Root>
        <Section>
          <Horizontal gap={1}>
            <Snowflake size={2} color="var(--c-arena-primary)" />
            <BrandS>
              <FormattedMessage
                defaultMessage="General"
                description="PlayMissions - General section title"
              />
            </BrandS>
          </Horizontal>
          {setPlayTasks.map(task => (
            <PlayMissionsTaskRow
              key={task.id}
              task={task}
              onClaim={
                isType(task, 'Task')
                  ? () => {
                      onClaim(task);
                      refetch?.();
                    }
                  : undefined
              }
              loading={loadingTaskId === task.id}
            />
          ))}
        </Section>
      </Root>
    </>
  );
};

PlayMissions.fragments = {
  currentUser: gql`
    fragment PlayMissions_currentUser on CurrentUser {
      slug
      setPlayTasks(sport: $sport) {
        id
        __typename
        ...PlayMissionsTaskRow_taskInterface
        ...TaskClaimedDialogs_taskInterface
      }
    }
    ${PlayMissionsTaskRow.fragments.taskInterface}
    ${TaskClaimedDialogs.fragments.taskInterface}
  ` as TypedDocumentNode<PlayMissions_currentUser>,
};
